import { useState } from "react";
import { CornerDownRight, Loader2, MessageCirclePlus, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamPost } from "@/lib/api";
import { Markdown } from "@/components/Bits";
import { SaveButton } from "@/components/SaveToPlaybook";

const Turn = ({ question, answer, testId, pending, save }) => (
  <div className="border-l-2 border-pinkb/40 pl-4 fade-up" data-testid={testId}>
    <p className="font-medium text-sm flex items-start gap-2"><CornerDownRight className="h-4 w-4 mt-0.5 text-pinkb shrink-0" />{question}</p>
    <div className="mt-3">{answer ? <Markdown>{answer}</Markdown> : pending && <p className="text-sm text-muted-foreground animate-pulse">Thinking about your follow-up…</p>}</div>
    {save && <div className="mt-2">{save}</div>}
  </div>
);

const NewTopicNote = ({ question, onAskNew, onDismiss, qid }) => (
  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm" data-testid={`followup-new-topic-${qid}`}>
    <p>That sounds like a new topic, so it's best asked as a new question. That way it gets its own full answer.</p>
    <Button size="sm" variant="outline" onClick={() => { onAskNew(question); onDismiss(); }} data-testid={`followup-ask-new-${qid}`} className="rounded-full mt-3">Ask it as a new question</Button>
  </div>
);

export const FollowUps = ({ q, onChange, onAskNew, usage, reloadUsage }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [live, setLive] = useState(null);
  const [newTopic, setNewTopic] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 5) return toast.error("Tell me a little more first.");
    const question = text.trim();
    setLive({ question, answer: "" }); setText(""); setNewTopic(null);
    try {
      await streamPost(`/questions/${q.id}/followups`, { question }, (ev) => {
        if (ev.type === "delta") setLive((l) => ({ ...l, answer: l.answer + ev.content }));
        if (ev.type === "done") {
          if (ev.record?.new_topic) setNewTopic(question); else if (ev.record) onChange(ev.record);
          setLive(null);
        }
        if (ev.type === "error") { toast.error(ev.message); setText(question); setLive(null); }
      });
    } catch (err) { toast.error(err.message); setText(question); setLive(null); }
    reloadUsage?.();
  };
  const cap = usage?.followups_daily_limit || 0;
  const left = cap ? Math.max(cap - (usage?.followups_used_today || 0), 0) : null;

  const items = q.followups || [];
  return (
    <div className="mt-6 space-y-5" data-testid={`followups-${q.id}`}>
      {items.map((f) => <Turn key={f.id} question={f.question} answer={f.answer} testId={`followup-${f.id}`}
        save={!f.answer?.startsWith("This one's outside my lane") && <SaveButton qid={q.id} fid={f.id} testId={`followup-save-${f.id}`} />} />)}
      {live && !newTopic && <Turn question={live.question} answer={live.answer} pending testId={`followup-live-${q.id}`} />}
      {newTopic && <NewTopicNote question={newTopic} onAskNew={onAskNew} onDismiss={() => setNewTopic(null)} qid={q.id} />}
      {!open && !live && (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`followup-open-${q.id}`} className="rounded-full">
          <MessageCirclePlus className="h-4 w-4 mr-1.5" />Ask a follow-up
        </Button>
      )}
      {open && (
        <form onSubmit={submit} className="rounded-xl border border-border bg-background p-3" data-testid={`followup-form-${q.id}`}>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} disabled={!!live} data-testid={`followup-input-${q.id}`}
            placeholder="e.g. What would that look like for a 40-cover venue?" className="bg-transparent border-0 focus-visible:ring-0 resize-none p-1 text-sm" />
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground" data-testid={`followup-allowance-${q.id}`}>{left === 0 ? "You've used today's follow-ups. They reset at midnight." : `Free, and they don't use your monthly questions.${left !== null ? ` ${left} left today.` : ""}`}</span>
            <Button type="submit" size="sm" disabled={!!live || left === 0} data-testid={`followup-submit-${q.id}`} className="rounded-full bg-navy hover:bg-navy/90 text-white">
              {live ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
